import { CollectionSurveyQuestions } from '@src/generated/hooks';
import { validateEmail } from '../validation/email';
import { validateMultiChoice } from '../validation/multi_choice';
import { validateNumber } from '../validation/number';
import { validatePhone } from '../validation/phone';
import { validateSingleChoice } from '../validation/single_choice';
import { validateSubQuestion } from '../validation/sub_question';
import { validateText } from '../validation/text';

/**
 * @name getErrorMessage
 * @description The way the survey was designed in such a way that each
 * question should hold all the information it needs to validate itself.
 * This makes it easy to just pass the whole question in and go from there.
 * Each question is also designed to support a custom error message per
 * question.
 */
export const getErrorMessage = (question: CollectionSurveyQuestions) => {
  // Use the given error message by default
  if (question.error_message) return question.error_message;

  switch (question.type) {
    case 'email':
      return 'A valid email address is required.';

    case 'multi_choice': {
      const { choices_min: min, choices_max: max } = question;

      if (!min && !max) return 'At least one choice is required.';
      if (!min) return `Please select 1 - ${max} options.`;
      if (!max) return `Please select at least ${min} option.`;

      return `Please select between ${min} - ${max} options.`;
    }

    case 'number': {
      const { number_min: min, number_max: max } = question;

      if (!min && !max) return `Please please enter a numeric value`;
      if (!min) return `Please select 1 - ${max} options.`;
      if (!max) return `Please select at least ${min} option`;

      return `Please please enter a numeric value between ${min} and ${max}`;
    }

    case 'single_choice':
      return 'Please select one option.';

    // case 'text':
    // case 'textarea':
    default:
      return 'This field is required.';
  }
};

/**
 * @name validateQuestionType
 * @description A convenience function that hands each question type off to
 * the correct function for validation
 */
export const validateQuestionType = (
  question: CollectionSurveyQuestions,
  answers: Record<string, any> = {}
): string => {
  const answer = answers[question.name];

  switch (question.type) {
    case 'email':
      return validateEmail(question, answer);
    case 'multi_choice':
      return validateMultiChoice(question, answer);
    case 'number':
      return validateNumber(question, answer);
    case 'phone':
      return validatePhone(question, answer);
    case 'single_choice':
      return validateSingleChoice(question, answer);
    case 'sub_questions':
      return validateSubQuestion(question, answers);
    case 'text':
    case 'textarea':
      return validateText(question, answer);

    // Validation is built-in ~ or ~ unavailable
    case 'image':
    case 'products':
    case 'progress':
    case 'query_param':
    case 'uuid':
    case 'prefill_multiple':
    case 'prefill_single':
      return '';

    default:
      throw new Error(`${question.type} not implemented yet`);
  }
};
